<script setup lang="ts">
import { X } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

import downloadCsvExample from '../utils/downloadCsvExample';

interface Emits {
  (_e: 'update:modelValue', _value: boolean): void;
  (_e: 'submit', _ids: number[]): void;
}

interface Props {
  cessionId: number;
  isLoading?: boolean;
  modelValue: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
});

const emit = defineEmits<Emits>();

const activeTab = ref<'csv' | 'ids'>('ids');
const idsInput = ref('');
const csvFile = ref<File | undefined>(undefined);
const csvFinancingIds = ref<number[]>([]);
const idsError = ref('');
const csvError = ref('');

const show = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const parsedIds = computed(() => {
  if (activeTab.value === 'ids') {
    const input = idsInput.value.trim();
    if (!input) return [];

    const ids = input
      .split(/[\n,]+/)
      .map((id) => id.trim())
      .filter((id) => id)
      .map((id) => Number.parseInt(id, 10))
      .filter((id) => !Number.isNaN(id));

    return ids;
  }
  return csvFinancingIds.value;
});

const canSubmit = computed(() => {
  if (props.isLoading) return false;
  if (activeTab.value === 'ids') {
    return parsedIds.value.length > 0;
  }
  return csvFinancingIds.value.length > 0;
});

watch(activeTab, () => {
  idsError.value = '';
  csvError.value = '';
});

watch(idsInput, () => {
  idsError.value = '';
});

function handleClose(): void {
  if (props.isLoading) return;
  show.value = false;
  activeTab.value = 'ids';
  idsInput.value = '';
  csvFile.value = undefined;
  csvFinancingIds.value = [];
  idsError.value = '';
  csvError.value = '';
}

function handleFileUpload(event: Event): void {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (!file) return;

  if (!file.name.endsWith('.csv')) {
    csvError.value = 'Por favor, selecione um arquivo CSV válido.';
    return;
  }

  csvFile.value = file;
  csvError.value = '';

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const text = e.target?.result as string;
      const lines = text.split('\n').map((line) => line.trim()).filter((line) => line);
      const ids = lines.map((line) => {
        const num = Number.parseInt(line, 10);
        if (Number.isNaN(num)) {
          throw new Error(`ID inválido: ${line}`);
        }
        return num;
      });

      if (ids.length === 0) {
        throw new Error('O arquivo CSV está vazio.');
      }

      csvFinancingIds.value = ids;
      csvError.value = '';
    } catch (error: any) {
      csvError.value = error.message || 'Erro ao processar o arquivo CSV.';
      csvFinancingIds.value = [];
    }
  };
  reader.readAsText(file);
}

function handleSubmit(): void {
  if (!canSubmit.value) return;

  if (activeTab.value === 'ids') {
    if (parsedIds.value.length === 0) {
      idsError.value = 'Por favor, insira ao menos um ID válido.';
      return;
    }
    emit('submit', parsedIds.value);
  } else {
    if (csvFinancingIds.value.length === 0) {
      csvError.value = 'Por favor, carregue um arquivo CSV com IDs válidos.';
      return;
    }
    emit('submit', csvFinancingIds.value);
  }
}

function removeCsvFile(): void {
  csvFile.value = undefined;
  csvFinancingIds.value = [];
  csvError.value = '';
  const fileInput = document.getElementById('csvFileInput') as HTMLInputElement;
  if (fileInput) {
    fileInput.value = '';
  }
}
</script>

<template>
  <div
    class="modal modal-blur fade"
    :class="{ show }"
    :style="{ display: show ? 'block' : 'none' }"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Adicionar Financiamentos</h5>
          <button
            type="button"
            class="btn-close"
            :disabled="isLoading"
            @click="handleClose"
          />
        </div>
        <div class="modal-body">
          <ul class="nav nav-tabs mb-3" role="tablist">
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'ids' }"
                type="button"
                role="tab"
                :disabled="isLoading"
                @click="activeTab = 'ids'"
              >
                Por IDs
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === 'csv' }"
                type="button"
                role="tab"
                :disabled="isLoading"
                @click="activeTab = 'csv'"
              >
                Via CSV
              </button>
            </li>
          </ul>

          <div v-if="activeTab === 'ids'" class="tab-pane">
            <div class="mb-3">
              <label class="form-label" for="financingIds">
                IDs dos Financiamentos
                <span class="text-danger">*</span>
              </label>
              <textarea
                id="financingIds"
                v-model="idsInput"
                class="form-control"
                :class="{ 'is-invalid': idsError }"
                rows="6"
                placeholder="Insira os IDs separados por vírgula ou quebra de linha
Exemplo:
123456
234567
345678"
                :disabled="isLoading"
              />
              <small class="form-hint">
                Insira os IDs dos financiamentos separados por vírgula ou quebra de linha.
              </small>
              <div v-if="idsError" class="invalid-feedback d-block">
                {{ idsError }}
              </div>
              <div v-if="!idsError && parsedIds.length > 0" class="mt-2">
                <span class="badge bg-info-lt">
                  {{ parsedIds.length }} ID(s) válido(s)
                </span>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'csv'" class="tab-pane">
            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-start">
                <label class="form-label" for="csvFileInput">
                  Arquivo CSV
                  <span class="text-danger">*</span>
                </label>
                <button
                  type="button"
                  class="btn btn-link px-0"
                  :disabled="isLoading"
                  aria-label="Baixar exemplo de arquivo CSV"
                  @click="downloadCsvExample"
                >
                  Baixar exemplo
                </button>
              </div>
              <input
                id="csvFileInput"
                type="file"
                class="form-control"
                :class="{ 'is-invalid': csvError }"
                accept=".csv"
                :disabled="isLoading"
                @change="handleFileUpload"
              />
              <small class="form-hint">
                O arquivo deve conter uma coluna com IDs de financiamento, um por linha, sem cabeçalho.
              </small>

              <div v-if="csvError" class="invalid-feedback d-block">
                {{ csvError }}
              </div>

              <div v-if="csvFile && !csvError" class="alert alert-success mt-2">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{{ csvFile.name }}</strong> - {{ csvFinancingIds.length }} financiamento(s) carregado(s)
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-ghost-danger"
                    :disabled="isLoading"
                    @click="removeCsvFile"
                  >
                    <X :size="16" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="isLoading"
            @click="handleClose"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!canSubmit"
            @click="handleSubmit"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" />
            Adicionar
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="show" class="modal-backdrop fade show" />
</template>
